import React, { useState, useEffect } from 'react';
import {
Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
Paper, TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions,
IconButton, Tooltip, Grid, Typography, Pagination, Slide, Fade,
InputAdornment, Snackbar, Alert, Box, CircularProgress, Chip
} from '@mui/material';
import {
Add as AddIcon,
Edit as EditIcon,
Delete as DeleteIcon,
Search as SearchIcon,
Refresh as RefreshIcon,
Visibility as ViewIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { TEXT_CONNECTION_ERROR } from '../utilities/Text';
import {
APIHandlerList, APIHandlerStore, APIHandlerUpdate, APIHandlerDelete, headers
} from '../utilities/APIs';
import useCustomToast from '../hooks/useToast';
import axios from 'axios';
import { formatNumberWithComma } from '../utilities/Functions';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../utilities/Permission';

const Handler = () => {
const theme = useTheme();
const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

// State
const [loading, setLoading] = useState(true);
const [openAdd, setOpenAdd] = useState(false);
const [openEdit, setOpenEdit] = useState(false);
const [openView, setOpenView] = useState(false);
const [openDelete, setOpenDelete] = useState(false);
const [currentHandler, setCurrentHandler] = useState(null);
const [page, setPage] = useState(1);
const [rowsPerPage, setRowsPerPage] = useState(10);
const [searchTerm, setSearchTerm] = useState('');
const [error, setError] = useState(null);
const [postStatus, setPostStatus] = useState('ADD');
const [updateId, setUpdateId] = useState('');
const showToast = useCustomToast();
const [pageState, setPageState] = useState({ pageData: [] });
const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

// Form data — only Name, Country, Area, Contact, Email
const [formData, setFormData] = useState({
    name: '',
    country: '',
    area: '',
    contact: '',
    email: ''
});

// Permissions
const [userRole, setUserRole] = useState('');
useEffect(() => {
    const role = getUserRole();
    setUserRole(role);
}, []);

const hasPermission = (permission) => {
    return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
};

useEffect(() => {
    fetchData();
}, []);

const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
    const response = await axios.get(APIHandlerList, { headers });
    const data = Array.isArray(response.data.data) ? response.data.data.reverse() : [];
    setPageState(prev => ({ ...prev, pageData: data }));
    } catch (error) {
    let errorMessage = "Failed to load handler data";
    if (error.response) {
        errorMessage = error.response.data.message || error.message;
    } else if (error.code === "ERR_NETWORK") {
        errorMessage = TEXT_CONNECTION_ERROR;
    }
    setError(errorMessage);
    console.log(errorMessage);
    showToast("Error", errorMessage, "error");
    } finally {
    setLoading(false);
    }
};

// Filter handlers based on search term
const filteredHandlers = pageState.pageData.filter(handler => {
    return (
    handler.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    handler.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
    handler.area.toLowerCase().includes(searchTerm.toLowerCase()) ||
    handler.contact.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (handler.email && handler.email.toLowerCase().includes(searchTerm.toLowerCase()))
    );
});

// Pagination
const count = Math.ceil(filteredHandlers.length / rowsPerPage);
const indexOfLastRow = page * rowsPerPage;
const indexOfFirstRow = indexOfLastRow - rowsPerPage;
const currentRows = filteredHandlers.slice(indexOfFirstRow, indexOfLastRow);

const handleChangePage = (event, newPage) => setPage(newPage);
const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
};

const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
};

const handleOpenAdd = () => {
    setPostStatus("ADD");
    setFormData({ name: '', country: '', area: '', contact: '', email: '' });
    setOpenAdd(true);
};

const handleOpenView = (handler) => {
    setCurrentHandler(handler);
    setOpenView(true);
};

const handleOpenEdit = (handler) => {
    setFormData({
    name: handler.name,
    country: handler.country,
    area: handler.area,
    contact: handler.contact,
    email: handler.email || ''
    });
    setCurrentHandler(handler);
    setUpdateId(handler.id);
    setPostStatus("UPDATE");
    setOpenEdit(true);
};

const handleOpenDelete = (handler) => {
    setCurrentHandler(handler);
    setUpdateId(handler.id);
    setPostStatus("DELETE");
    setOpenDelete(true);
};

const handleClose = (event, reason) => {
    if (reason === 'backdropClick') return;
    setOpenAdd(false);
    setOpenEdit(false);
    setOpenView(false);
    setOpenDelete(false);
};

const handleRefresh = () => {
    setLoading(true);
    fetchData();
    setSnackbar({ open: true, message: 'Handler data refreshed', severity: 'info' });
};

const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

const validateForm = () => {
    const errors = [];
    if (!formData.name.trim()) errors.push('Name is required');
    if (!formData.country.trim()) errors.push('Country is required');
    if (!formData.area.trim()) errors.push('Area is required');
    if (!formData.contact.trim()) errors.push('Contact is required');
    if (!formData.email.trim()) errors.push('Email is required');

    if (formData.contact.trim() && !/^[\d\s\-+()]+$/.test(formData.contact)) {
    errors.push('Please enter a valid contact number');
    }

    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    errors.push('Please enter a valid email address');
    }

    if (errors.length > 0) {
    showToast("Validation Error", errors.join('\n'), "warning");
    return false;
    }
    return true;
};

const handleSubmit = async (e) => {
    e.preventDefault();
    if (postStatus !== "DELETE") {
    if (!validateForm()) return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('name', formData.name);
    formDataToSend.append('country', formData.country);
    formDataToSend.append('area', formData.area);
    formDataToSend.append('contact', formData.contact);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('updateId', updateId);

    try {
    let postRequest;
    if (postStatus === "ADD") {
        postRequest = await axios.post(APIHandlerStore, formDataToSend, { headers });
    } else if (postStatus === "UPDATE") {
        postRequest = await axios.post(APIHandlerUpdate, formDataToSend, { headers });
    } else if (postStatus === "DELETE") {
        postRequest = await axios.post(APIHandlerDelete, formDataToSend, { headers });
    }

    const response = postRequest.data.status;
    const message = postRequest.data.message;

    if (response === "success") {
        handleClose();
        fetchData();
        showToast("Success", message, "success");
    } else {
        showToast("Error", "Unknown Server Error, Record Not Created", "error");
    }
    } catch (error) {
    if (error.response) {
        if (error.response.status === 422) {
        const errors = error.response.data.errors;
        let errorMessages = [];
        for (const field in errors) {
            errorMessages.push(...errors[field]);
        }
        showToast("Validation Error", errorMessages.join("\n"), "error");
        } else {
        showToast("Error", error.response.data.message || "An error occurred", "error");
        }
    } else if (error.code === "ERR_NETWORK") {
        showToast("Connection Error", TEXT_CONNECTION_ERROR, "error");
    } else {
        showToast("Error", "An unexpected error occurred", "error");
    }
    }
};

if (loading) {
    return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px" flexDirection="column" gap={2}>
        <CircularProgress />
        <Typography variant="body1">Loading handlers...</Typography>
    </Box>
    );
}

if (error) {
    return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px" flexDirection="column" gap={2}>
        <Typography variant="h6" color="error">Error Loading Data</Typography>
        <Typography variant="body2" color="textSecondary">{error}</Typography>
        <Button variant="contained" color="primary" onClick={fetchData} startIcon={<RefreshIcon />}>Retry</Button>
    </Box>
    );
}

return (
    <div style={{ marginTop: 15, padding: isMobile ? '8px' : '24px' }}>
    <Slide direction="down" in={true} mountOnEnter unmountOnExit>
        <Paper elevation={3} style={{ padding: isMobile ? '16px' : '24px', borderRadius: '12px', marginBottom: '24px' }}>
        <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
            <Grid item>
            <Typography variant="subtitle1">
                Handlers Total:
                <Chip label={formatNumberWithComma(pageState.pageData.length)} color="primary" variant="outlined" style={{ marginLeft: '8px' }} />
            </Typography>
            </Grid>
            <Grid item>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleOpenAdd} style={{ borderRadius: '8px' }}>
                Add Handler
            </Button>
            </Grid>
        </Grid>

        <Grid container spacing={2} style={{ marginTop: '8px' }}>
            <Grid item xs={12} sm={6} md={4}>
            <TextField
                fullWidth variant="outlined" placeholder="Search handlers..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>), style: { borderRadius: '8px' } }}
            />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
            <TextField
                fullWidth select value={rowsPerPage} onChange={handleChangeRowsPerPage}
                variant="outlined" style={{ borderRadius: '8px' }} SelectProps={{ native: true }}
            >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
            </TextField>
            </Grid>
            <Grid item xs={12} md={4} style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Tooltip title="Refresh data">
                <IconButton onClick={handleRefresh} style={{ backgroundColor: theme.palette.grey[200], borderRadius: '8px' }}>
                <RefreshIcon />
                </IconButton>
            </Tooltip>
            </Grid>
        </Grid>
        </Paper>
    </Slide>

    <Fade in={!loading} timeout={500}>
        <div>
        <TableContainer component={Paper} elevation={3} style={{ borderRadius: '12px', marginBottom: '24px' }}>
            <Table>
            <TableHead>
                <TableRow style={{ backgroundColor: theme.palette.primary.main }}>
                <TableCell style={{ color: 'white', fontWeight: 'bold' }}>#</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Name</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Country</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Area</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Contact</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Email</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {currentRows.length > 0 ? (
                currentRows.map((handler, index) => (
                    <StyledTableRow key={handler.id}>
                    <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                    <TableCell><Typography variant="body2" fontWeight="medium">{handler.name}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{handler.country}</Typography></TableCell>
                    <TableCell>
                        <Chip label={handler.area} color="primary" variant="outlined" size="small" />
                    </TableCell>
                    <TableCell><Typography variant="body2">{handler.contact}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{handler.email || '-'}</Typography></TableCell>
                    <TableCell>
                        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                        <Tooltip title="View Details">
                            <ActionButton variant="contained" color="info" size="small" onClick={() => handleOpenView(handler)} style={{ backgroundColor: '#17a2b8' }}>
                            <ViewIcon fontSize="small" />
                            </ActionButton>
                        </Tooltip>
                        {hasPermission(PERMISSIONS.ALL) && (
                            <>
                            <Tooltip title="Edit">
                                <ActionButton variant="contained" color="primary" size="small" onClick={() => handleOpenEdit(handler)}>
                                <EditIcon fontSize="small" />
                                </ActionButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                                <ActionButton variant="contained" color="error" size="small" onClick={() => handleOpenDelete(handler)}>
                                <DeleteIcon fontSize="small" />
                                </ActionButton>
                            </Tooltip>
                            </>
                        )}
                        </div>
                    </TableCell>
                    </StyledTableRow>
                ))
                ) : (
                <TableRow>
                    <TableCell colSpan={7} style={{ textAlign: 'center', padding: '40px' }}>
                    <Typography variant="subtitle1">
                        {searchTerm ? 'No matching handlers found' : 'No handler records available'}
                    </Typography>
                    {!searchTerm && (
                        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleOpenAdd} style={{ marginTop: '16px' }}>
                        Add First Handler
                        </Button>
                    )}
                    </TableCell>
                </TableRow>
                )}
            </TableBody>
            </Table>
        </TableContainer>

        {filteredHandlers.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
            <Pagination count={count} page={page} onChange={handleChangePage} color="primary" shape="rounded" showFirstButton showLastButton />
            </div>
        )}
        </div>
    </Fade>

    {/* Add Handler Dialog */}
    <Dialog open={openAdd} onClose={handleClose} fullWidth maxWidth="sm" TransitionComponent={Slide} transitionDuration={300}>
        <DialogTitle style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>Add New Handler</Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: '20px' }}>
        <form onSubmit={handleSubmit}>
            <Box>
            <Grid item xs={12}>
                <TextField fullWidth label="Name" name="name" value={formData.name} autoComplete="off" onChange={handleInputChange} required variant="outlined" helperText="Enter handler's full name" />
            </Grid><br />
            <Grid item xs={12}>
                <TextField fullWidth label="Country" name="country" value={formData.country} autoComplete="off" onChange={handleInputChange} required variant="outlined" helperText="Enter country (e.g., Uganda, Kenya)" />
            </Grid><br />
            <Grid item xs={12}>
                <TextField fullWidth label="Area" name="area" value={formData.area} autoComplete="off" onChange={handleInputChange} required variant="outlined" helperText="Enter area/region (e.g., Kampala, Nairobi)" />
            </Grid><br />
            <Grid item xs={12}>
                <TextField fullWidth label="Contact" name="contact" value={formData.contact} autoComplete="off" onChange={handleInputChange} required variant="outlined" helperText="Enter contact number" InputProps={{ startAdornment: <InputAdornment position="start">📞</InputAdornment> }} />
            </Grid><br />
            <Grid item xs={12}>
                <TextField fullWidth label="Email" name="email" type="email" value={formData.email} autoComplete="off" onChange={handleInputChange} required variant="outlined" helperText="Enter email address" InputProps={{ startAdornment: <InputAdornment position="start">✉️</InputAdornment> }} />
            </Grid>
            </Box>
        </form>
        </DialogContent>
        <DialogActions style={{ borderTop: `1px solid ${theme.palette.divider}`, padding: '16px 24px' }}>
        <Button onClick={handleClose} color="error" variant="outlined">Cancel</Button>
        <Button onClick={handleSubmit} color="primary" variant="contained">Save Handler</Button>
        </DialogActions>
    </Dialog>

    {/* View Handler Dialog */}
    <Dialog open={openView} onClose={handleClose} fullWidth maxWidth="sm" TransitionComponent={Slide} transitionDuration={300}>
        <DialogTitle style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>Handler Details</Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: '20px' }}>
        {currentHandler && (
            <Grid container spacing={3}>
            <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="bold" color="textSecondary">Name:</Typography>
                <Typography variant="body1" sx={{ ml: 2, p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>{currentHandler.name}</Typography>
            </Grid>
            <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="bold" color="textSecondary">Country:</Typography>
                <Typography variant="body1" sx={{ ml: 2, p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>{currentHandler.country}</Typography>
            </Grid>
            <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="bold" color="textSecondary">Area:</Typography>
                <Box sx={{ ml: 2, p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                <Chip label={currentHandler.area} color="primary" variant="outlined" />
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="bold" color="textSecondary">Contact:</Typography>
                <Typography variant="body1" sx={{ ml: 2, p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>{currentHandler.contact}</Typography>
            </Grid>
            <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="bold" color="textSecondary">Email:</Typography>
                <Typography variant="body1" sx={{ ml: 2, p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>{currentHandler.email || 'Not specified'}</Typography>
            </Grid>
            </Grid>
        )}
        </DialogContent>
        <DialogActions style={{ borderTop: `1px solid ${theme.palette.divider}`, padding: '16px 24px' }}>
        <Button onClick={handleClose} color="primary" variant="contained">Close</Button>
        </DialogActions>
    </Dialog>

    {/* Edit Handler Dialog */}
    <Dialog open={openEdit} onClose={handleClose} fullWidth maxWidth="sm" TransitionComponent={Slide} transitionDuration={300}>
        <DialogTitle style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>Edit Handler</Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: '20px' }}>
        <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
            <Grid item xs={12}>
                <TextField fullWidth label="Name" name="name" value={formData.name} autoComplete="off" onChange={handleInputChange} required variant="outlined" />
            </Grid>
            <Grid item xs={12}>
                <TextField fullWidth label="Country" name="country" value={formData.country} autoComplete="off" onChange={handleInputChange} required variant="outlined" />
            </Grid>
            <Grid item xs={12}>
                <TextField fullWidth label="Area" name="area" value={formData.area} autoComplete="off" onChange={handleInputChange} required variant="outlined" />
            </Grid>
            <Grid item xs={12}>
                <TextField fullWidth label="Contact" name="contact" value={formData.contact} autoComplete="off" onChange={handleInputChange} required variant="outlined" InputProps={{ startAdornment: <InputAdornment position="start">📞</InputAdornment> }} />
            </Grid>
            <Grid item xs={12}>
                <TextField fullWidth label="Email" name="email" type="email" value={formData.email} autoComplete="off" onChange={handleInputChange} required variant="outlined" InputProps={{ startAdornment: <InputAdornment position="start">✉️</InputAdornment> }} />
            </Grid>
            </Grid>
        </form>
        </DialogContent>
        <DialogActions style={{ borderTop: `1px solid ${theme.palette.divider}`, padding: '16px 24px' }}>
        <Button onClick={handleClose} color="error" variant="outlined">Cancel</Button>
        <Button onClick={handleSubmit} color="primary" variant="contained">Update Handler</Button>
        </DialogActions>
    </Dialog>

    {/* Delete Confirmation Dialog */}
    <Dialog open={openDelete} onClose={handleClose} maxWidth="xs" fullWidth TransitionComponent={Slide} transitionDuration={300}>
        <DialogTitle style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>Confirm Delete</Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: '20px', textAlign: 'center' }}>
        <Typography variant="body1" style={{ marginBottom: '16px' }}>Are you sure you want to delete this handler?</Typography>
        <Typography variant="h6" color="error" style={{ fontWeight: 600 }}>{currentHandler?.name}</Typography>
        <Typography variant="body2" style={{ marginTop: '8px' }}>Country: {currentHandler?.country}</Typography>
        <Typography variant="body2" style={{ marginTop: '4px' }}>Email: {currentHandler?.email || '-'}</Typography>
        <Typography variant="caption" display="block" style={{ marginTop: '8px' }}>This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions style={{ borderTop: `1px solid ${theme.palette.divider}`, padding: '16px 24px' }}>
        <Button onClick={handleClose} variant="outlined" color="primary">Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="error" startIcon={<DeleteIcon />}>Delete</Button>
        </DialogActions>
    </Dialog>

    {/* Snackbar */}
    <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} TransitionComponent={Fade}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
        {snackbar.message}
        </Alert>
    </Snackbar>
    </div>
);
};

export default Handler;

// Styled components
const StyledTableRow = styled(TableRow)(({ theme }) => ({
'&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
'&:hover': { backgroundColor: theme.palette.action.selected, transition: 'background-color 0.3s ease' },
}));

const ActionButton = styled(Button)(({ theme }) => ({
margin: theme.spacing(0.5),
minWidth: 'auto',
}));